import { useState, useEffect, useMemo } from 'react';
import { calculateTimeDifference, type TimeUnit } from './timeUtils';

interface UseCountdownOptions {
  /**
   * Update interval in milliseconds (default: 60000ms = 1 minute)
   */
  interval?: number;
  /**
   * Whether the countdown is active
   */
  isActive?: boolean;
}

interface UseCountdownReturn {
  timeLeft: TimeUnit;
  isExpired: boolean;
}

/**
 * Custom hook for countdown functionality
 * @param targetDate - The target date to count down to
 * @param options - Configuration options
 * @returns Countdown state and expiry status
 */
const useCountdown = (
  targetDate: Date,
  options: UseCountdownOptions = {},
): UseCountdownReturn => {
  const { interval = 60000, isActive = true } = options;
  
  const [currentTime, setCurrentTime] = useState(new Date());

  // Memoize the time calculation
  const timeLeft = useMemo(
    () => calculateTimeDifference(targetDate, currentTime),
    [targetDate, currentTime],
  );

  const isExpired = useMemo(
    () => targetDate.getTime() <= currentTime.getTime(),
    [targetDate, currentTime],
  );

  useEffect(() => {
    if (!isActive || isExpired) {
      return undefined;
    }

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, interval);

    return () => clearInterval(timer);
  }, [interval, isActive, isExpired]);

  return { timeLeft, isExpired };
};

export default useCountdown;
